import { routeStore } from '../state/routeStore';
import { connectionStatus, lastWaypointAt } from '../state/connection';
import { buildHeroCopy } from '../lib/heroCopy';
import { formatLastUpdate } from '../lib/relativeTime';
import { TransportChip } from './TransportChip';
import { Avatar, type AvatarStatus } from './Avatar';
import { useEffect, useState } from 'preact/hooks';
import clsx from 'clsx';
// ETA is intentionally not a concept in ArriveSafe — the mobile app doesn't
// surface one and the SPA matches that. The eta.ts helpers are gone from
// this file but kept in lib/ in case product wants to reintroduce them later.

export function StatusHero() {
  const route = routeStore.value;

  // Tick a local clock once per second so relative timestamps update without
  // requiring a new socket event. Cheap; only one timer for the whole hero.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const handle = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(handle);
  }, []);

  const finishedAt = formatFinishTime(route?.finished_at);
  const { headline, subtitle, announcement } = buildHeroCopy(route, finishedAt);
  const updated = formatLastUpdate(lastWaypointAt.value ? new Date(lastWaypointAt.value) : null, now);

  // Map our connection signal to the Avatar's badge taxonomy. Emergency
  // overrides everything else; finished routes are handled by ArrivalState,
  // so here we just expose the connection state.
  const conn = connectionStatus.value;
  const status: AvatarStatus =
    route?.emergency?.state === 'active'
      ? 'emergency'
      : conn === 'idle'
        ? 'connecting'
        : (conn as AvatarStatus);
  const isWarning = conn === 'stale' || conn === 'offline';

  return (
    <div class="pointer-events-auto inline-flex w-full max-w-xl items-start gap-3 rounded-2xl bg-surface/95 px-4 py-3 shadow-md backdrop-blur-sm border border-border/50">
      <Avatar
        photoUrl={route?.creator?.photo?.medium}
        name={route?.creator?.name ?? route?.creator?.username}
        size={44}
        status={status}
      />
      <div class="flex flex-col gap-1 min-w-0">
        <h1 class="text-base font-semibold leading-tight text-primary" aria-live="polite">
          {headline}
        </h1>
        {/* @username right under the headline. Disambiguates common first
            names ("Tim", "Anna") for watchers who follow multiple people. */}
        {route?.creator?.username && (
          <p class="text-xs text-text-secondary leading-tight">@{route.creator.username}</p>
        )}
        {subtitle && <p class="text-sm text-text-secondary">{subtitle}</p>}
        <div
          class={clsx(
            'flex flex-wrap items-center gap-1.5 mt-0.5 text-xs',
            isWarning ? 'text-warning font-medium' : 'text-text-secondary',
          )}
        >
          <TransportChip type={route?.transportation?.currentType} />
          {updated && <span>{updated}</span>}
        </div>
      </div>
      <span class="sr-only">{announcement}</span>
    </div>
  );
}

function formatFinishTime(finishedAt: string | null | undefined): string | null {
  if (!finishedAt) return null;
  const date = new Date(finishedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
